import { ILocation } from 'src/data/locations';
import globalAgent from 'src/agent';
import { URLS } from 'src/constants/urls';

interface GeoNamesResponse {
    geonames: Array<{
        geonameId: number;
        name: string;
        countryName: string;
        adminName1?: string;
        lat: string;
        lng: string;
        fcl: string; // feature class
        fcode: string; // feature code
        population?: number;
    }>;
}

interface YandexGeocoderResponse {
    response: {
        GeoObjectCollection: {
            featureMember: Array<{
                GeoObject: {
                    name: string;
                    description: string;
                    Point: {
                        pos: string; // "lng lat"
                    };
                    metaDataProperty: {
                        GeocoderMetaData: {
                            Address: {
                                Components: Array<{
                                    kind: string;
                                    name: string;
                                }>;
                            };
                        };
                    };
                };
            }>;
        };
    };
}

interface BackendLocationEntry {
    kind: string;
    geoname_id: number | null;
    name: string;
    display_name: string;
    formatted_address?: string;
    address?: string;
    country_code: string | null;
    country_name: string | null;
    region_name: string | null;
    city_name?: string | null;
    district_name?: string | null;
    street_name?: string | null;
    house?: string | null;
    latitude: number | null;
    longitude: number | null;
    population: number | null;
    precision?: string | null;
    source: string;
}

interface BackendLocationSearchResponse {
    results: BackendLocationEntry[];
}

class GeoService {
    private readonly GEONAMES_USERNAME = process.env.REACT_APP_GEONAMES_USERNAME || '';
    private readonly YANDEX_API_KEY = process.env.REACT_APP_YANDEX_API_KEY || '';

    /**
     * Поиск локаций через наш backend-справочник cities_light.
     */
    async searchCitiesBackend(query: string, maxResults = 20, countries?: string[]): Promise<ILocation[]> {
        if (!query.trim()) return [];

        try {
            const response = await globalAgent.get<BackendLocationSearchResponse>(URLS.LOCATION_SEARCH, {
                params: {
                    q: query,
                    limit: maxResults,
                    countries: countries?.filter(Boolean).join(',') || undefined,
                },
            });

            return (response.data.results || []).map((item) => ({
                id: `${item.kind}_${item.geoname_id || item.display_name}`,
                name: item.name,
                display_name: item.display_name,
                country: item.country_name || item.country_code || '',
                country_code: item.country_code,
                region: item.region_name || undefined,
                kind: item.kind,
                geoname_id: item.geoname_id,
                source: item.source,
                coordinates: {
                    lat: item.latitude || 0,
                    lng: item.longitude || 0,
                },
            }));
        } catch {
            return [];
        }
    }

    /**
     * Проверка точного адреса через backend-geocoder.
     */
    async geocodeAddress(query: string, maxResults = 5, countries?: string[]): Promise<ILocation[]> {
        if (!query.trim()) return [];

        try {
            const response = await globalAgent.get<BackendLocationSearchResponse>(URLS.LOCATION_GEOCODE, {
                params: {
                    q: query,
                    limit: maxResults,
                    countries: countries?.filter(Boolean).join(',') || undefined,
                },
            });

            return (response.data.results || []).map((item) => ({
                id: `address_${item.latitude || ''}_${item.longitude || ''}_${item.display_name}`,
                name: item.name || item.display_name,
                display_name: item.display_name,
                formatted_address: item.formatted_address || item.display_name,
                address: item.address || item.formatted_address || item.display_name,
                country: item.country_name || item.country_code || '',
                country_code: item.country_code,
                region: item.region_name || item.district_name || undefined,
                city_name: item.city_name,
                district_name: item.district_name,
                street_name: item.street_name,
                house: item.house,
                kind: item.kind || 'address',
                geoname_id: item.geoname_id,
                precision: item.precision,
                source: item.source,
                coordinates: {
                    lat: item.latitude || 0,
                    lng: item.longitude || 0,
                },
            }));
        } catch {
            return [];
        }
    }

    /**
     * Поиск городов через GeoNames API
     */
    async searchCities(query: string, maxRows = 20): Promise<ILocation[]> {
        if (!query.trim() || !this.GEONAMES_USERNAME) return [];

        try {
            const response = await fetch(
                `https://secure.geonames.org/searchJSON?q=${encodeURIComponent(query)}&maxRows=${maxRows}&username=${
                    this.GEONAMES_USERNAME
                }&featureClass=P&featureCode=PPL&featureCode=PPLA&featureCode=PPLA2&featureCode=PPLA3&featureCode=PPLA4&orderby=population&isNameRequired=true`
            );

            if (!response.ok) {
                throw new Error(`GeoNames API error: ${response.status}`);
            }

            const data: GeoNamesResponse = await response.json();

            return data.geonames.map((city) => ({
                id: `geoname_${city.geonameId}`,
                name: city.name,
                country: city.countryName,
                region: city.adminName1 || undefined,
                coordinates: {
                    lat: parseFloat(city.lat),
                    lng: parseFloat(city.lng),
                },
            }));
        } catch (error) {
            console.warn('GeoNames API error:', error);
            return [];
        }
    }

    /**
     * Поиск городов через Яндекс.Геокодер API
     */
    async searchCitiesYandex(query: string, maxResults = 20): Promise<ILocation[]> {
        if (!query.trim() || !this.YANDEX_API_KEY) return [];

        try {
            const response = await fetch(
                `https://geocode-maps.yandex.ru/1.x/?apikey=${this.YANDEX_API_KEY}&geocode=${encodeURIComponent(
                    query
                )}&format=json&results=${maxResults}&kind=locality`
            );

            if (!response.ok) {
                throw new Error(`Yandex Geocoder API error: ${response.status}`);
            }

            const data: YandexGeocoderResponse = await response.json();

            return data.response.GeoObjectCollection.featureMember.map((item, index) => {
                const geoObject = item.GeoObject;
                const [lng, lat] = geoObject.Point.pos.split(' ').map(Number);

                // Извлекаем компоненты адреса
                const components = geoObject.metaDataProperty.GeocoderMetaData.Address.Components;
                const country = components.find((c) => c.kind === 'country')?.name || '';
                const region =
                    components.find((c) => c.kind === 'province')?.name ||
                    components.find((c) => c.kind === 'area')?.name ||
                    components.find((c) => c.kind === 'district')?.name;

                return {
                    id: `yandex_${index}`,
                    name: geoObject.name,
                    country,
                    region,
                    coordinates: { lat, lng },
                };
            });
        } catch (error) {
            console.warn('Yandex Geocoder API error:', error);
            return [];
        }
    }

    /**
     * Комбинированный поиск городов
     */
    async searchCitiesCombined(query: string, maxResults = 20): Promise<ILocation[]> {
        if (!query.trim()) return [];

        // Сначала пробуем наш backend-справочник, чтобы фронт и анализ ниши жили в одной гео-модели.
        let results = await this.searchCitiesBackend(query, maxResults);

        // Если backend-справочник пуст для региона, оставляем прежний внешний fallback.
        if (results.length === 0) {
            results = await this.searchCities(query, maxResults);
        }

        // Если GeoNames не дал результатов, пробуем Яндекс
        if (results.length === 0) {
            results = await this.searchCitiesYandex(query, maxResults);
        }

        // Убираем дубликаты по названию и стране
        const uniqueResults = results.filter(
            (location, index, self) =>
                index ===
                self.findIndex(
                    (l) =>
                        l.name.toLowerCase() === location.name.toLowerCase() &&
                        l.country.toLowerCase() === location.country.toLowerCase()
                )
        );

        return uniqueResults.slice(0, maxResults);
    }

    /**
     * Получение популярных городов по стране
     */
    async getPopularCitiesByCountry(countryCode: string, maxResults = 50): Promise<ILocation[]> {
        if (!this.GEONAMES_USERNAME) return [];
        try {
            const response = await fetch(
                `https://secure.geonames.org/searchJSON?country=${countryCode}&maxRows=${maxResults}&username=${this.GEONAMES_USERNAME}&featureClass=P&featureCode=PPL&featureCode=PPLA&featureCode=PPLA2&featureCode=PPLA3&featureCode=PPLA4&orderby=population&isNameRequired=true`
            );

            if (!response.ok) {
                throw new Error(`GeoNames API error: ${response.status}`);
            }

            const data: GeoNamesResponse = await response.json();

            return data.geonames.map((city) => ({
                id: `geoname_${city.geonameId}`,
                name: city.name,
                country: city.countryName,
                region: city.adminName1 || undefined,
                coordinates: {
                    lat: parseFloat(city.lat),
                    lng: parseFloat(city.lng),
                },
            }));
        } catch (error) {
            console.warn('GeoNames API error:', error);
            return [];
        }
    }

    /**
     * Получение информации о городе по координатам (обратный геокодинг)
     */
    async reverseGeocode(lat: number, lng: number): Promise<ILocation | null> {
        if (!this.GEONAMES_USERNAME) return null;
        try {
            const response = await fetch(
                `https://secure.geonames.org/findNearbyPlaceNameJSON?lat=${lat}&lng=${lng}&username=${this.GEONAMES_USERNAME}&maxRows=1`
            );

            if (!response.ok) {
                throw new Error(`GeoNames API error: ${response.status}`);
            }

            const data = await response.json();

            if (data.geonames && data.geonames.length > 0) {
                const city = data.geonames[0];
                return {
                    id: `geoname_${city.geonameId}`,
                    name: city.name,
                    country: city.countryName,
                    region: city.adminName1 || undefined,
                    coordinates: { lat, lng },
                };
            }

            return null;
        } catch (error) {
            console.warn('GeoNames reverse geocoding error:', error);
            return null;
        }
    }
}

export default new GeoService();
